import React, { forwardRef, useRef } from 'react';
import { FlagFilled } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { Tooltip } from 'shared/components/ui/tooltip';
import { tooltipDelay } from 'shared/constants';

import * as Styled from './event.style';
import { EventTime } from './event-time';

export type EventCardProps = {
  title: string;
  // The leading indicator (checkbox / progress circle / icon) including its own
  // slot — kept as an element prop so each card supplies its own indicator while
  // sharing this layout.
  leading: React.ReactNode;
  // Trailing affordance (currently the calendar-sync badge), supplied by the
  // card so this layout stays free of any data dependency.
  trailing?: React.ReactNode;
  color?: string | null;
  timeText?: string;
  completed?: boolean;
  // Not passed by task cards: an uncompleted task stays full opacity when past.
  isPast?: boolean;
  isDeadline?: boolean;
  // drops the tonal fill for a colored outline; the card stays otherwise identical
  outlined?: boolean;
  numberOfLines: number;
  onClick?: (event: React.MouseEvent) => void;
  onContextMenu?: (event: React.MouseEvent) => void;
};

// Shared layout for the calendar event cards: leading indicator + title + time,
// with the same color/typography/past/completed treatment. Cards differ only in
// the leading element and their data/interaction logic.
export const EventCard = forwardRef<HTMLDivElement, EventCardProps>(
  (
    {
      title,
      leading,
      trailing,
      color,
      timeText,
      completed,
      isPast,
      isDeadline,
      outlined,
      numberOfLines,
      onClick,
      onContextMenu,
    },
    ref,
  ) => {
    const titleRef = useRef<HTMLSpanElement>(null);

    return (
      <Styled.Container
        ref={ref}
        $color={color}
        $outlined={outlined}
        onClick={onClick}
        onContextMenu={onContextMenu}
      >
        <Styled.Content>
          {leading}
          <Styled.TextColumn>
            <Styled.Title
              ref={titleRef}
              $completed={!!completed}
              // Reserve a line for the time below so it isn't pushed out of a
              // medium-height card.
              $numberOfLines={
                timeText ? Math.max(1, numberOfLines - 1) : numberOfLines
              }
              $color={color}
              $past={isPast}
            >
              {title}
            </Styled.Title>
            {!!timeText && (
              <EventTime
                timeText={timeText}
                completed={completed}
                color={color}
                past={isPast}
              />
            )}
          </Styled.TextColumn>
          {isDeadline && (
            <Styled.DeadlineIconContainer
              $color={color ?? undefined}
              $completed={!!completed}
              $past={isPast}
            >
              <Icon icon={FlagFilled} />
            </Styled.DeadlineIconContainer>
          )}
          <Styled.TrailingSlot $completed={!!completed}>
            {trailing}
          </Styled.TrailingSlot>
        </Styled.Content>

        <Tooltip
          referenceElement={titleRef}
          label={title}
          delay={tooltipDelay}
        />
      </Styled.Container>
    );
  },
);
